<agr-form-dialog
  (alt)="alt()"
  (cancel)="cancel()"
  (confirm)="process()"
  [contentSpacing]="false"
  [data]="data"
  [formGroup]="formGroup"
>
  <div class="dialog-content">
    @if (fertilizers$ | async; as fertilizers) {
      @if (fertilizers.length > 0) {
        <mat-accordion>
          @for (fertilizer of fertilizers; track fertilizer.cropAid.id; let fertilizerIndex = $index) {
            <mat-expansion-panel
              #panel
              (closed)="onPanelClosed(fertilizer.cropAid.id)"
              (opened)="onPanelOpened(fertilizer.cropAid.id)"
              [disabled]="!fertilizer.isOrderable"
              [expanded]="fertilizer.isOrderable && isPanelExpanded(fertilizer.cropAid.id)"
            >
              <mat-expansion-panel-header class="custom-header">
                <div class="header-row-container">
                  <div class="header-row">
                    <div class="product-header">
                      <div class="icon-container">
                        <img
                          alt="Fertilizer product icon"
                          class="package-icon"
                          height="24"
                          src="../../../../assets/icons/package.svg"
                          tabindex="0"
                          width="24"
                        />
                      </div>
                      <strong>{{ fertilizer.cropAid.name }}</strong>
                    </div>
                  </div>

                  @if (!fertilizer.isOrderable) {
                    <div class="header-row">
                      <div class="non-orderable-warning-container">
                        <div class="header-warning">
                          <mat-icon color="warn">warning</mat-icon>
                          <span class="warning-text">
                            {{ 'report.productNotOrderable' | translate }}
                            {{ 'report.contactCustomerService' | translate }}
                          </span>
                        </div>
                      </div>
                    </div>
                  }

                  @if (!panel.expanded && fertilizer.isOrderable) {
                    <div class="header-row">
                      <div class="header-summary">
                        <div class="summary-item">
                          <span class="label">{{ 'report.requisite' | translate }}</span>
                          <span class="value-bold"
                            >{{ fertilizer.requisiteAmount }} {{ fertilizer.cropAid.unitOfMeasurement }}</span
                          >
                        </div>
                        <div class="summary-item">
                          <span class="label">{{ 'report.productInventory' | translate }}</span>
                          <span class="value-bold"
                            >{{ fertilizer.customerProductInventoryAmount }}
                            {{ fertilizer.cropAid.unitOfMeasurement }}</span
                          >
                        </div>
                        <div class="summary-item">
                          <span class="label">{{ 'report.order' | translate }}</span>
                          <span class="value-bold"
                            >{{ fertilizer.orderLinesTotalAmount }} {{ fertilizer.cropAid.unitOfMeasurement }}</span
                          >
                        </div>
                        <div class="summary-item">
                          <span class="label">{{ 'report.difference' | translate }}</span>
                          <span
                            [ngClass]="{
                              'value-negative': isNegative(
                                getDifference(
                                  fertilizer.requisiteAmount,
                                  fertilizer.customerProductInventoryAmount,
                                  fertilizer.orderLinesTotalAmount
                                )
                              ),
                            }"
                            class="value-bold"
                          >
                            {{
                              getDifference(
                                fertilizer.requisiteAmount,
                                fertilizer.customerProductInventoryAmount,
                                fertilizer.orderLinesTotalAmount
                              ) | agrNumber: '1.0-2'
                            }}
                            {{ fertilizer.cropAid.unitOfMeasurement }}
                          </span>
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </mat-expansion-panel-header>

              <div class="panel-content">
                @if (fertilizer.orderLines && fertilizer.orderLines.length > 0) {
                  <div class="order-lines-grid">
                    <div class="column-headers">
                      <div class="header-left">{{ 'report.package' | translate }}</div>
                      <div class="header-right">{{ 'report.order' | translate }}</div>
                    </div>

                    <div class="order-lines-rows">
                      @for (orderLine of fertilizer.orderLines; track orderLine.id; let orderLineIndex = $index) {
                        <div class="order-line-row">
                          <div class="package-name">
                            @if (orderLine.package) {
                              {{ orderLine.package.name }}
                            } @else {
                              {{ 'report.package' | translate }} ID: {{ orderLine.packageId }}
                            }
                          </div>

                          <div class="order-actions">
                            <div class="quantity-input">
                              <div class="editable-stock">
                                <mat-form-field appearance="outline">
                                  <input
                                    [formControl]="
                                      getOrderLineCountControlById(orderLine.id, fertilizer.formIndex, orderLineIndex)
                                    "
                                    [step]="getOrderLineIncreaseOrderUnitQuantity(orderLine)"
                                    matInput
                                    min="0"
                                    type="number"
                                  />
                                </mat-form-field>
                                <span class="stock-unit">{{ fertilizer.cropAid.unitOfMeasurement }}</span>
                              </div>
                            </div>
                            @if (fertilizer.orderLines.length > 1) {
                              <button
                                (click)="deleteOrderLine(orderLine.id)"
                                [attr.aria-label]="'report.deleteOrderLine' | translate"
                                class="delete-button"
                                color="warn"
                                mat-icon-button
                              >
                                <mat-icon>delete</mat-icon>
                              </button>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </div>

                  @if (!isAddingPackageFor(fertilizer)) {
                    <button
                      (click)="addPackage(fertilizer)"
                      [disabled]="!(hasAvailablePackages(fertilizer) | async)"
                      class="add-package-btn"
                      color="primary"
                      mat-button
                    >
                      <mat-icon>add</mat-icon>
                      {{ 'report.addPackage' | translate }}
                    </button>
                  }

                  @if (isAddingPackageFor(fertilizer)) {
                    <div class="package-selector-section">
                      <h4>{{ 'report.selectPackage' | translate }}</h4>

                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>{{ 'report.selectPackage' | translate }}</mat-label>
                        <mat-select [(value)]="selectedPackageId" required>
                          @for (package of availablePackagesForCurrentFertilizer; track package.id) {
                            <mat-option [value]="package.id">
                              {{ package.name }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>

                      <div class="package-selector-actions">
                        <button (click)="cancelPackageSelection()" mat-button>
                          {{ 'cancel' | translate }}
                        </button>
                        <button
                          (click)="confirmPackageSelection()"
                          [disabled]="!selectedPackageId"
                          color="primary"
                          mat-raised-button
                        >
                          {{ 'report.addPackage' | translate }}
                        </button>
                      </div>
                    </div>
                  }

                  <div class="summary-section">
                    <div class="summary-row">
                      <span class="summary-label">{{ 'report.requisite' | translate }}:</span>
                      <span class="summary-value">
                        {{ fertilizer.requisiteAmount | agrNumber: '1.0-2' }}
                        {{ fertilizer.cropAid.unitOfMeasurement }}
                      </span>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">{{ 'report.stock' | translate }}:</span>
                      <div class="summary-value editable-stock">
                        <mat-form-field appearance="outline" class="stock-input">
                          <input
                            [formControl]="getStockControl(fertilizer.formIndex)"
                            matInput
                            min="0"
                            step="0.01"
                            type="number"
                          />
                        </mat-form-field>
                        <span class="stock-unit">{{ fertilizer.cropAid.unitOfMeasurement }}</span>
                      </div>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">{{ 'report.totalToOrder' | translate }}:</span>
                      <span class="summary-value">
                        {{
                          fertilizer.requisiteAmount - fertilizer.customerProductInventoryAmount | agrNumber: '1.0-2'
                        }}
                        {{ fertilizer.cropAid.unitOfMeasurement }}
                      </span>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">{{ 'report.orderTitle' | translate }}:</span>
                      <span class="summary-value positive">
                        {{ fertilizer.orderLinesTotalAmount | agrNumber: '1.0-2' }}
                        {{ fertilizer.cropAid.unitOfMeasurement }}
                      </span>
                    </div>
                    <div class="summary-row">
                      <span class="summary-label">{{ 'report.difference' | translate }}:</span>
                      <span
                        [ngClass]="{
                          negative:
                            getDifference(
                              fertilizer.requisiteAmount,
                              fertilizer.customerProductInventoryAmount,
                              fertilizer.orderLinesTotalAmount
                            ) < 0,
                          positive:
                            getDifference(
                              fertilizer.requisiteAmount,
                              fertilizer.customerProductInventoryAmount,
                              fertilizer.orderLinesTotalAmount
                            ) >= 0,
                        }"
                        class="summary-value"
                        >{{
                          getDifference(
                            fertilizer.requisiteAmount,
                            fertilizer.customerProductInventoryAmount,
                            fertilizer.orderLinesTotalAmount
                          ) | agrNumber: '1.0-2'
                        }}
                        {{ fertilizer.cropAid.unitOfMeasurement }}
                      </span>
                    </div>
                  </div>
                } @else {
                  <div class="no-order-lines">
                    <span class="no-data-text">{{ 'report.noOrderLinesAvailable' | translate }}</span>
                  </div>
                }
              </div>
            </mat-expansion-panel>
          }
        </mat-accordion>

        <div class="notification notification-warning">
          <mat-icon>warning</mat-icon>
          <div [innerHTML]="'report.fertilizerProposalDisclaimer' | translate" class="disclaimer"></div>
        </div>
      } @else {
        <div class="no-data">
          <span class="no-data-text">{{ 'noFertilizersAvailable' | translate }}</span>
        </div>
      }
    }
  </div>
</agr-form-dialog>
